import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { m, useInView } from 'framer-motion';
import {
  ArrowLeft,
  BadgeCheck,
  BarChart3,
  CreditCard,
  DollarSign,
  Infinity,
  ShoppingCart,
  Tag,
  TrendingDown,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { WireframeBackground } from '@/components/WireframeBackground';
import { MeshpitLogo } from '@/components/MeshpitLogo';
import { useAuth } from '@/contexts/AuthContext';

// ---------------------------------------------------------------------------
// Pricing config — all values read from env vars with safe fallbacks
// ---------------------------------------------------------------------------

function env(key: string, fallback: number): number {
  const raw = (import.meta.env as Record<string, string>)[key];
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

const SUBSCRIPTION_YEARLY = env('VITE_PRICE_SUBSCRIPTION_YEARLY', 24);
const SUBSCRIPTION_MONTHLY = +(SUBSCRIPTION_YEARLY / 12).toFixed(2);

const TIER1_UP_TO = env('VITE_FEE_TIER1_UP_TO', 500);
const TIER1_RATE = env('VITE_FEE_TIER1_RATE', 10);
const TIER2_UP_TO = env('VITE_FEE_TIER2_UP_TO', 2000);
const TIER2_RATE = env('VITE_FEE_TIER2_RATE', 8);
const TIER3_RATE = env('VITE_FEE_TIER3_RATE', 5);

// ---------------------------------------------------------------------------
// Animation helpers
// ---------------------------------------------------------------------------

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

function FadeIn({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <m.div
      ref={ref}
      variants={fadeUp}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      transition={{ duration: 0.45, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </m.div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function PricingFeature({
  icon: Icon,
  text,
}: {
  icon: React.ComponentType<{ className?: string }>;
  text: string;
}) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground/10">
        <Icon className="h-3 w-3 text-foreground" />
      </span>
      <span className="text-sm text-muted-foreground">{text}</span>
    </li>
  );
}

interface FeeTierCardProps {
  label: string;
  range: string;
  rate: number;
  highlight?: boolean;
  index: number;
}

function FeeTierCard({
  label,
  range,
  rate,
  highlight,
  index,
}: FeeTierCardProps) {
  return (
    <FadeIn delay={0.08 + index * 0.06}>
      <div
        className={`flex flex-col rounded-xl border p-6 ${
          highlight
            ? 'border-foreground/20 bg-card shadow-sm'
            : 'border-border bg-card/50'
        }`}
      >
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">
          {label}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">{range}</p>
        <p className="mt-4 flex items-baseline gap-1">
          <span className="text-5xl font-bold tracking-tight text-foreground">
            {rate}%
          </span>
          <span className="text-sm text-muted-foreground">fee</span>
        </p>
        <p className="mt-3 text-xs text-muted-foreground/60">
          of gross sales in this tier, billed monthly
        </p>
      </div>
    </FadeIn>
  );
}

// ---------------------------------------------------------------------------
// FAQ data
// ---------------------------------------------------------------------------

const FAQ = [
  {
    q: 'Do I need a subscription to use Meshpit?',
    a: 'No. Creating models, publishing them for free, and exploring community models is always free. A Seller subscription is only required to charge money for your models.',
  },
  {
    q: 'When is the platform fee charged?',
    a: 'The fee is calculated on your total gross sales each calendar month and deducted before your Stripe payout. You will see an itemised breakdown in your dashboard.',
  },
  {
    q: 'How does the tiered fee work exactly?',
    a: `The tiers apply to different portions of your monthly revenue — similar to a tax bracket. For example, if you earn $${TIER2_UP_TO.toLocaleString()} in a month, you pay ${TIER1_RATE}% on the first $${TIER1_UP_TO.toLocaleString()}, ${TIER2_RATE}% on the next $${(TIER2_UP_TO - TIER1_UP_TO).toLocaleString()}, and ${TIER3_RATE}% on anything above that.`,
  },
  {
    q: 'What payment processor is used?',
    a: 'All payments and payouts are handled by Stripe. Meshpit never stores your card details. Buyers pay via Stripe Checkout; sellers receive payouts directly to their connected Stripe account.',
  },
  {
    q: 'Can I cancel my Seller subscription?',
    a: 'Yes, at any time. Your paid models will be automatically unpublished at the end of your billing period. Your models and all their versions remain in your account — you can re-subscribe at any point to re-activate them.',
  },
];

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function Pricing() {
  const { isAuthenticated, signInWithGitHub } = useAuth();

  const handleGetStarted = () => {
    if (!isAuthenticated) {
      signInWithGitHub();
    }
  };

  return (
    <div className="relative min-h-full bg-background">
      <WireframeBackground />
      <div className="relative z-10 mx-auto max-w-6xl px-6 pb-32">
        {/* ---------------------------------------------------------------- */}
        {/* HEADER                                                            */}
        {/* ---------------------------------------------------------------- */}
        <div className="py-16">
          <FadeIn>
            <Link
              to="/"
              className="mb-8 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to home
            </Link>
          </FadeIn>

          <FadeIn delay={0.04}>
            <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">
              Simple, transparent pricing
            </p>
          </FadeIn>

          <FadeIn delay={0.08}>
            <h1 className="max-w-2xl text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
              Pricing
            </h1>
          </FadeIn>

          <FadeIn delay={0.13}>
            <p className="mt-4 max-w-xl text-lg leading-relaxed text-muted-foreground">
              Free to use, free to publish. Pay only when you sell — and only a
              small fraction of what you earn.
            </p>
          </FadeIn>
        </div>

        <FadeIn>
          <Separator />
        </FadeIn>

        {/* ---------------------------------------------------------------- */}
        {/* SECTION A — SELLER SUBSCRIPTION                                  */}
        {/* ---------------------------------------------------------------- */}
        <section className="py-16" id="subscription">
          <FadeIn>
            <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">
              Seller plan
            </p>
            <h2 className="text-3xl font-bold tracking-tight text-foreground">
              Sell your models
            </h2>
            <p className="mt-3 max-w-xl text-base text-muted-foreground">
              One flat yearly subscription unlocks the ability to set prices on
              your models and collect payments from buyers anywhere in the
              world.
            </p>
          </FadeIn>

          <FadeIn delay={0.1} className="mt-10">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
              {/* Pricing card */}
              <div className="flex w-full max-w-sm flex-col rounded-2xl border border-foreground/20 bg-card p-8 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">
                  Seller subscription
                </p>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="text-5xl font-bold tracking-tight text-foreground">
                    ${SUBSCRIPTION_MONTHLY}
                  </span>
                  <span className="text-sm text-muted-foreground">/ month</span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Billed as{' '}
                  <span className="font-medium text-foreground">
                    ${SUBSCRIPTION_YEARLY}/year
                  </span>
                </p>

                <Separator className="my-6" />

                <ul className="flex flex-col gap-3">
                  <PricingFeature
                    icon={Infinity}
                    text="Unlimited paid model publishing"
                  />
                  <PricingFeature
                    icon={Tag}
                    text="Set any price on any model"
                  />
                  <PricingFeature
                    icon={CreditCard}
                    text="Stripe payouts directly to your bank"
                  />
                  <PricingFeature
                    icon={BarChart3}
                    text="Sales analytics dashboard"
                  />
                  <PricingFeature
                    icon={ShoppingCart}
                    text="Buyer licence management"
                  />
                  <PricingFeature icon={Zap} text="Priority model processing" />
                </ul>

                <div className="mt-8">
                  {isAuthenticated ? (
                    <Button className="w-full" size="lg">
                      Upgrade to Seller
                    </Button>
                  ) : (
                    <Button
                      className="w-full"
                      size="lg"
                      onClick={handleGetStarted}
                    >
                      Get started
                      <span aria-hidden className="ml-1">
                        →
                      </span>
                    </Button>
                  )}
                  <p className="mt-3 text-center text-xs text-muted-foreground/60">
                    Free models always free.{' '}
                    <Link
                      to="/terms"
                      className="underline-offset-4 hover:text-muted-foreground hover:underline"
                    >
                      Terms apply.
                    </Link>
                  </p>
                </div>
              </div>

              {/* What's always free */}
              <div className="flex flex-col gap-4 pt-1">
                <p className="text-sm font-medium text-foreground">
                  Always free — no subscription needed
                </p>
                <ul className="flex flex-col gap-3">
                  {[
                    'Create and edit unlimited models',
                    'Publish models for free (no paywall)',
                    'Explore and use community models',
                    'Fork and remix any public model',
                    'Full 3D viewer & parameter controls',
                    'AI-assisted code generation',
                    'Export STL / OBJ / AMF / 3MF files',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/50" />
                      <span className="text-sm text-muted-foreground">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </FadeIn>
        </section>

        <FadeIn>
          <Separator />
        </FadeIn>

        {/* ---------------------------------------------------------------- */}
        {/* SECTION B — REVENUE SHARE                                        */}
        {/* ---------------------------------------------------------------- */}
        <section className="py-16" id="revenue-share">
          <FadeIn>
            <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">
              Revenue share
            </p>
            <h2 className="text-3xl font-bold tracking-tight text-foreground">
              Platform fee on sales
            </h2>
            <p className="mt-3 max-w-xl text-base text-muted-foreground">
              In addition to the Seller subscription, a small percentage of your
              gross monthly sales is shared with the platform. The more you
              earn, the lower your effective rate — like a tax bracket in your
              favour.
            </p>
          </FadeIn>

          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <FeeTierCard
              index={0}
              label="Starter tier"
              range={`$0 – $${TIER1_UP_TO.toLocaleString()} / month`}
              rate={TIER1_RATE}
            />
            <FeeTierCard
              index={1}
              label="Growth tier"
              range={`$${TIER1_UP_TO.toLocaleString()} – $${TIER2_UP_TO.toLocaleString()} / month`}
              rate={TIER2_RATE}
              highlight
            />
            <FeeTierCard
              index={2}
              label="Scale tier"
              range={`$${TIER2_UP_TO.toLocaleString()}+ / month`}
              rate={TIER3_RATE}
            />
          </div>

          <FadeIn delay={0.25}>
            <div className="mt-8 flex items-start gap-3 rounded-xl border border-border bg-muted/40 px-5 py-4">
              <TrendingDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Tiers are{' '}
                <span className="font-medium text-foreground">marginal</span> —
                each rate applies only to the portion of sales within that
                bracket, not to your total. Your effective rate decreases
                automatically as you grow.
              </p>
            </div>
          </FadeIn>
        </section>

        <FadeIn>
          <Separator />
        </FadeIn>

        {/* ---------------------------------------------------------------- */}
        {/* SECTION C — FAQ                                                  */}
        {/* ---------------------------------------------------------------- */}
        <section className="py-16" id="faq">
          <FadeIn>
            <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">
              FAQ
            </p>
            <h2 className="text-3xl font-bold tracking-tight text-foreground">
              Common questions
            </h2>
          </FadeIn>

          <dl className="mt-10 flex flex-col divide-y divide-border">
            {FAQ.map(({ q, a }, i) => (
              <FadeIn key={q} delay={0.05 * i}>
                <div className="py-6">
                  <dt className="text-sm font-medium text-foreground">{q}</dt>
                  <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {a}
                  </dd>
                </div>
              </FadeIn>
            ))}
          </dl>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* CTA FOOTER                                                       */}
        {/* ---------------------------------------------------------------- */}
        <FadeIn>
          <Separator className="mb-16" />
        </FadeIn>

        <section className="flex flex-col items-center py-16 text-center">
          <FadeIn>
            <div className="mb-6 flex items-center justify-center">
              <MeshpitLogo className="h-10 w-10 text-muted-foreground" />
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Start building — it's free
            </h2>
            <p className="mt-4 max-w-md text-base text-muted-foreground">
              No credit card required. Upgrade to Seller whenever you're ready
              to monetise your work.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to="/new">
                <Button size="lg" className="gap-2 px-7">
                  <DollarSign className="h-4 w-4" />
                  Start Creating
                </Button>
              </Link>
              <Link to="/explore">
                <Button size="lg" variant="outline" className="gap-2 px-7">
                  Explore Models
                </Button>
              </Link>
            </div>
            <p className="mt-6 text-xs text-muted-foreground/60">
              <Link
                to="/"
                className="underline-offset-4 hover:text-muted-foreground/90 hover:underline"
              >
                Home
              </Link>
              {' · '}
              <Link
                to="/terms"
                className="underline-offset-4 hover:text-muted-foreground/90 hover:underline"
              >
                Terms of Service
              </Link>
              {' · '}
              <Link
                to="/changelog"
                className="underline-offset-4 hover:text-muted-foreground/90 hover:underline"
              >
                Changelog
              </Link>
            </p>
          </FadeIn>
        </section>
      </div>
    </div>
  );
}
